import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Text, Button, TextInput } from 'grommet';
import PropTypes from 'prop-types';
import Loader from 'granite-admin/core/components/Loader';
import InfoText from 'common/components/infoText';
import FormField from 'granite-admin/core/components/FormField';
import Select from 'granite-admin/core/components/Select';
import bigCommerce from 'common/gateways/common-api.js';
import warehousesApi from 'bigCommerce/gateways/bigCommerce-api.js';
import { triggerInitialSync } from 'bigCommerce/controllers/bigCommerce';

const konectBaseUrl = process.env.REACT_APP_KONECT_REDIRECT_BASE_URL;

const StoreConfiguration = ({
  setComp,
  comp,
  InitialValuesStore,
  business,
  data,
  successToast,
  id,
  errorToast,
  setDisabledAll,
  setData,
}) => {
  const [marketplaceOptions, setMarketplaceOptions] = useState([]);
  const [warehouseData, setWarehouseData] = useState([]);
  const [datas, setDatas] = useState(InitialValuesStore?.[0]);
  const [loader, setLoader] = useState(true);
  const [channelRows, setChannelRows] = useState({});
  const [channels, setChannels] = useState([]);
  const [channelsLoader, setChannelsLoader] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const savedMarketplaces = useMemo(
    () => data?.settings?.dynamic?.quiversData?.marketplaces || [],
    [data?.settings?.dynamic?.quiversData?.marketplaces],
  );

  const store_name_mapping = useMemo(
    () => data?.settings?.dynamic?.quiversData?.store_name_mapping || {},
    [data?.settings?.dynamic?.quiversData?.store_name_mapping],
  );

  useEffect(() => {
    (async () => {
      try {
        const [mktData, whData] = await Promise.all([
          bigCommerce.getMarketplace(business),
          warehousesApi.getWarehouses(business),
        ]);
        setMarketplaceOptions(mktData || []);
        setWarehouseData(whData || []);
        console.log('marketplaceOptions loaded:', mktData?.length, mktData?.[0]);
        setLoader(false);
      } catch (e) {
        errorToast(e.errors?.title || 'Failed to load options');
      }
    })();
  }, [business, errorToast]);

  const loadChannels = useCallback(
    async store => {
      if (!store) return;
      setChannelsLoader(true);
      try {
        const result = await warehousesApi.getChannels(store.id);
        const channelList = result?.data || result || [];
        setChannels(channelList);

        // All saved entries for this store (flat array, one per channel)
        const savedEntries = savedMarketplaces.filter(m => m.store_id === store.id);
        const storeMapping = store_name_mapping[store.id] || {};

        const rows = {};
        channelList.forEach((ch, idx) => {
          // Match by channel_id first (new shape), then fall back to first entry for old single-store shape
          const saved =
            savedEntries.find(m => m.channel_id === String(ch.id)) ||
            (savedEntries.length === 1 && idx === 0 ? savedEntries[0] : null);

          // orderPrefix: new nested shape first, then old flat shape (marketplace_id key), then saved entry
          const newShapePrefix = storeMapping[String(ch.id)];
          const oldShapePrefix = saved?.marketplace_id ? (store_name_mapping[saved.marketplace_id] || '') : '';
          const orderPrefix = newShapePrefix !== undefined ? newShapePrefix : oldShapePrefix;

          rows[ch.id] = {
            marketplace_id: saved?.marketplace_id || '',
            marketplace_name: saved?.marketplace_name || '',
            warehouses: saved?.warehouses || [],
            orderPrefix,
          };
        });
        setChannelRows(rows);
      } catch (e) {
        errorToast(e.errors?.title || 'Failed to load channels');
      }
      setChannelsLoader(false);
    },
    [savedMarketplaces, store_name_mapping, errorToast],
  );

  useEffect(() => {
    if (!loader) loadChannels(datas);
  }, [datas, loader, loadChannels]);

  const setChannelField = (chId, fields) => {
    setChannelRows(prev => ({ ...prev, [chId]: { ...prev[chId], ...fields } }));
    setErrors(prev => {
      const next = { ...prev };
      Object.keys(fields).forEach(k => delete next[`${chId}_${k}`]);
      return next;
    });
  };

  // Returns marketplace options for a channel: currency-filtered, excluding marketplaces
  // already selected by OTHER channels. Always includes the current channel's saved value.
  const getOptionsForChannel = useCallback(
    (chId, currencyCode) => {
      // Only exclude IDs used by other channels that have a channel_id saved (new shape)
      // to avoid hiding options due to old single-store data where all channels shared one marketplace
      const savedEntries = savedMarketplaces.filter(m => m.store_id === datas?.id);
      const isNewShape = savedEntries.some(m => m.channel_id);

      const usedIds = isNewShape
        ? Object.entries(channelRows)
            .filter(([id, row]) => String(id) !== String(chId) && row.marketplace_id)
            .map(([, row]) => row.marketplace_id)
        : [];

      const base = marketplaceOptions.filter(
        m =>
          (!m.PriceTypeCurrency || !currencyCode || m.PriceTypeCurrency === currencyCode) &&
          !usedIds.includes(m.Id),
      );

      // Always include the currently saved one for this channel even if filtered out
      const current = marketplaceOptions.find(m => m.Id === channelRows[chId]?.marketplace_id);
      if (current && !base.find(m => m.Id === current.Id)) return [...base, current];
      return base;
    },
    [marketplaceOptions, channelRows, savedMarketplaces, datas],
  );

  const validate = () => {
    const errs = {};
    channels.forEach(ch => {
      const row = channelRows[ch.id] || {};
      if (!row.marketplace_id) errs[`${ch.id}_marketplace_id`] = 'Required';
      if (!row.orderPrefix) errs[`${ch.id}_orderPrefix`] = 'Required';
    });
    // Check duplicate order prefixes
    const prefixes = channels.map(ch => channelRows[ch.id]?.orderPrefix).filter(Boolean);
    const dupes = prefixes.filter((p, i) => prefixes.indexOf(p) !== i);
    if (dupes.length) {
      channels.forEach(ch => {
        if (dupes.includes(channelRows[ch.id]?.orderPrefix))
          errs[`${ch.id}_orderPrefix`] = 'Must be unique';
      });
    }
    return errs;
  };

  const onSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setDisabledAll(true);
    try {
      // Flat entries for current store — one per channel
      const currentStoreEntries = channels
        .filter(ch => channelRows[ch.id]?.marketplace_id)
        .map(ch => ({
          store_id: datas.id,
          sync_retailer_inventory: true,
          channel_id: String(ch.id),
          channel_name: ch.name,
          currency: ch.currency_code,
          marketplace_id: channelRows[ch.id].marketplace_id,
          marketplace_name: channelRows[ch.id].marketplace_name,
          warehouses: channelRows[ch.id].warehouses || [],
        }));

      // Keep entries from other stores, replace current store's entries
      const updatedMarketplaces = [
        ...savedMarketplaces.filter(m => m.store_id !== datas.id),
        ...currentStoreEntries,
      ];

      // Rebuild store_name_mapping as { store_id: { channel_id: orderPrefix } }
      // Only keep nested object entries (store hashes), drop all old flat UUID string keys
      const otherStoreMapping = {};
      Object.entries(store_name_mapping).forEach(([key, val]) => {
        if (typeof val === 'object' && val !== null && key !== datas.id)
          otherStoreMapping[key] = val;
      });

      const currentStoreMapping = {};
      channels.forEach(ch => {
        const row = channelRows[ch.id];
        if (row?.orderPrefix) currentStoreMapping[String(ch.id)] = row.orderPrefix;
      });

      const payload = {
        id,
        settings: {
          ...data?.settings?.dynamic,
          ...(!data?.settings?.dynamic?.dashboard_url && {
            dashboard_url: `${konectBaseUrl}bigcommerce/dashboard?business=${business}`,
          }),
          quiversData: {
            ...data?.settings?.dynamic?.quiversData,
            marketplaces: updatedMarketplaces,
            store_name_mapping: { ...otherStoreMapping, [datas.id]: currentStoreMapping },
          },
        },
        displayName: data?.displayName,
      };

      await bigCommerce.updateData({ business, payload });
      const res = await bigCommerce.getData({ id, business });
      setData(res);
      setDisabledAll(false);
      successToast('Data updated successfully');
      triggerInitialSync({ business, pluginId: id, errorToast });
    } catch (e) {
      errorToast(e.errors?.title || 'Failed to update data');
    }
    setSubmitting(false);
  };

  const isUnchanged = useMemo(() => {
    if (!channels.length) return true;
    const savedEntries = savedMarketplaces.filter(m => m.store_id === datas?.id);
    const storeMapping = store_name_mapping[datas?.id] || {};
    return channels.every((ch, idx) => {
      const saved =
        savedEntries.find(m => m.channel_id === String(ch.id)) ||
        (savedEntries.length === 1 && idx === 0 ? savedEntries[0] : null);
      const row = channelRows[ch.id] || {};
      const newShapePrefix = storeMapping[String(ch.id)];
      const oldShapePrefix = saved?.marketplace_id ? (store_name_mapping[saved.marketplace_id] || '') : '';
      const savedPrefix = newShapePrefix !== undefined ? newShapePrefix : oldShapePrefix;
      return (
        saved?.marketplace_id === row.marketplace_id &&
        savedPrefix === row.orderPrefix
      );
    });
  }, [channels, channelRows, savedMarketplaces, datas, store_name_mapping]);

  return (
    <Box width={'70%'} pad={{ top: 'large' }} border={{ color: 'lightgray', size: 'xsmall', side: 'top' }}>
      {loader ? (
        <Box fill justify="center" align="center"><Loader /></Box>
      ) : (
        <>
          <Box pad={{ bottom: 'medium' }}>
            <Text size="xlarge" margin={{ bottom: 'medium' }} weight="bold">
              Select the Store for which you want to configure Quivers Pay settings
            </Text>
            <Box
              direction="row"
              justify="between"
              margin={{ bottom: 'medium' }}
              border={{ color: 'lightgray', size: 'xsmall', side: 'bottom' }}
            >
              <Box
                width={'50%'}
                margin={{ bottom: 'medium' }}
                border={{ color: 'lightgray', size: 'xsmall', side: 'right' }}
              >
                <Box direction="row" gap="small">
                  <Text margin={{ bottom: 'small' }} weight={'bold'}>Select a Store</Text>
                  <InfoText text="Select the Store for which you want to configure Quivers Pay settings." />
                </Box>
                <Box width="80%">
                  <FormField name="id" noBorder width={'100%'}>
                    <Box border="1px solid black">
                      <Select
                        options={InitialValuesStore || []}
                        name="id"
                        onChange={e => setDatas(e?.value)}
                        value={datas?.id}
                        valueKey={{ key: 'id', reduce: false }}
                        labelKey="label"
                      />
                    </Box>
                  </FormField>
                </Box>
              </Box>
              <Box width={'40%'}>
                <Text margin={{ bottom: 'small' }} weight={'bold'}>Store Name</Text>
                <Box pad={'small'}>
                  <Text truncate="tip">{datas?.store_name || ''}</Text>
                </Box>
              </Box>
            </Box>
            <Text size="xlarge" margin={{ top: 'medium' }} weight="bold">
              Map each Channel to a Quivers Storefront and enter a unique Order Prefix.
            </Text>
          </Box>

          {channelsLoader ? (
            <Box fill justify="center" align="center"><Loader /></Box>
          ) : channels.length === 0 ? (
            <Box pad={{ vertical: 'medium' }}>
              <Text color="dark-4">No channels found for this store.</Text>
            </Box>
          ) : (
            <Box>
              {/* Header */}
              <Box
                direction="row"
                border={{ color: 'lightgray', size: 'xsmall', side: 'bottom' }}
                pad={{ bottom: 'small' }}
                margin={{ bottom: 'small' }}
              >
                <Box width="20%"><Text weight="bold">Channel</Text></Box>
                <Box width="15%"><Text weight="bold">Currency</Text></Box>
                <Box width="25%">
                  <Box direction="row" gap="xsmall">
                    <Text weight="bold">Quivers Storefront</Text>
                    <InfoText text="Only storefronts matching the channel currency are shown. Each channel must have a unique storefront." />
                  </Box>
                </Box>
                <Box width="20%">
                  <Box direction="row" gap="xsmall">
                    <Text weight="bold">Warehouses</Text>
                    <InfoText text="Warehouses linked to the selected storefront." />
                  </Box>
                </Box>
                <Box width="20%">
                  <Box direction="row" gap="xsmall">
                    <Text weight="bold">Order Prefix</Text>
                    <InfoText text="Unique prefix per channel visible alongside the order ID on Quivers." />
                  </Box>
                </Box>
              </Box>

              {channels.map(ch => {
                const row = channelRows[ch.id] || {};
                const options = getOptionsForChannel(ch.id, ch.currency_code);
                const currentMkt = marketplaceOptions.find(m => m.Id === row.marketplace_id);
                const optionsWithSelected =
                  currentMkt && !options.find(m => m.Id === currentMkt.Id)
                    ? [currentMkt, ...options]
                    : options;
                const linkedWarehouses = warehouseData
                  .filter(w => row.warehouses?.includes(w.Id))
                  .map(w => w.Name);

                return (
                  <Box
                    key={ch.id}
                    direction="row"
                    align="center"
                    pad={{ vertical: 'small' }}
                    border={{ color: 'lightgray', size: 'xsmall', side: 'bottom' }}
                  >
                    <Box width="20%"><Text truncate="tip">{ch.name}</Text></Box>
                    <Box width="15%"><Text>{ch.currency_code || '—'}</Text></Box>
                    <Box width="25%" pad={{ right: 'small' }}>
                      <Box border="1px solid black" width="180px">
                        <Select
                          placeholder="Select Storefront"
                          options={optionsWithSelected}
                          onChange={e => {
                            const selected = optionsWithSelected.find(m => m.Id === e.value);
                            if (selected) {
                              setChannelField(ch.id, {
                                marketplace_id: selected.Id,
                                marketplace_name: selected.Name,
                                warehouses: selected.Warehouses,
                              });
                            } else {
                              setChannelField(ch.id, { marketplace_id: '', marketplace_name: '', warehouses: [] });
                            }
                          }}
                          value={row.marketplace_id || ''}
                          valueKey={{ key: 'Id', reduce: true }}
                          labelKey="Name"
                          emptySearchMessage="No storefronts found"
                        />
                      </Box>
                      {errors[`${ch.id}_marketplace_id`] && (
                        <Text color="status-error">{errors[`${ch.id}_marketplace_id`]}</Text>
                      )}
                    </Box>
                    <Box width="20%" pad={{ right: 'small' }}>
                      <Box direction="row" wrap gap="xsmall">
                        {linkedWarehouses.length > 0 ? (
                          linkedWarehouses.map(name => (
                            <Box key={name} background="silver" border pad={{ horizontal: 'small', vertical: 'xsmall' }}>
                              <Text truncate="tip">{name}</Text>
                            </Box>
                          ))
                        ) : (
                          <Text color="dark-4">—</Text>
                        )}
                      </Box>
                    </Box>
                    <Box width="20%">
                      <Box border="1px solid black" width="150px">
                        <TextInput
                          placeholder="Order Prefix"
                          name={`orderPrefix_${ch.id}`}
                          value={row.orderPrefix || ''}
                          onChange={e => setChannelField(ch.id, { orderPrefix: e.target.value })}
                        />
                      </Box>
                      {errors[`${ch.id}_orderPrefix`] && (
                        <Text color="status-error">{errors[`${ch.id}_orderPrefix`]}</Text>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          <Box justify="end" gap="xlarge" direction="row" margin={{ top: 'large' }}>
            <Box align="end">
              <Button label="Back" primary onClick={() => setComp(comp - 1)} />
            </Box>
            <Box align="end">
              {isUnchanged ? (
                <Button
                  label="Next"
                  primary
                  onClick={() => setComp(comp + 1)}
                  disabled={channels.some(ch => !channelRows[ch.id]?.marketplace_id)}
                />
              ) : (
                <Button label={submitting ? 'Saving...' : 'Save'} primary onClick={onSubmit} disabled={submitting} />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

StoreConfiguration.propTypes = {
  setComp: PropTypes.func,
  comp: PropTypes.number,
  business: PropTypes.string,
  id: PropTypes.string,
  data: PropTypes.object,
  errorToast: PropTypes.func,
  setDisabledAll: PropTypes.func,
  setData: PropTypes.func,
  successToast: PropTypes.any,
  InitialValuesStore: PropTypes.any,
};

export default StoreConfiguration;
